import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';

import { ArrowLeftIcon, ArrowRightIcon } from '@motorway/mw-highway-code';

import LazyLoadImage from 'Components/shared/LazyLoadImage/LazyLoadImage';
import { IMAGES_ORDER } from 'Pages/vehicles/helpers/images.const';
import {
	triggerVehicleCardImageLoadedEvent,
	triggerVehicleCardNextImageClickedEvent,
	triggerVehicleCardPrevImageClickedEvent,
} from 'Services/analytics/events/vehicleListEvents';
import type { HeroImage, VehicleImageMeta, VehicleLeadImage } from 'Types/vehicleImage';

import styles from './VehicleCardGallery.module.scss';

interface GalleryImage {
	kind?: string;
	meta?: VehicleImageMeta;
	url?: string;
}

export interface VehicleCardGalleryProps {
	enquiryId: number;
	heroImages?: HeroImage[];
	imageWidth?: number;
	leadPhoto?: VehicleLeadImage;
	shouldApplyFadeOutStyle?: boolean;
	vehiclePhotoAlt?: string;
}

// Default card image width in px — matches VehicleCard layout.
const DEFAULT_IMAGE_WIDTH = 336;
const SWIPE_THRESHOLD = 30;
const DIRECTION_LOCK_THRESHOLD = 5;

type Direction = 1 | -1;

const VehicleCardGallery: React.FC<VehicleCardGalleryProps> = ({
	enquiryId,
	heroImages,
	imageWidth = DEFAULT_IMAGE_WIDTH,
	leadPhoto,
	shouldApplyFadeOutStyle,
	vehiclePhotoAlt,
}) => {
	const trackRef = useRef<HTMLDivElement>(null);
	const [currentIndex, setCurrentIndex] = useState(0);
	const [isLooping, setIsLooping] = useState(false);
	const [isAnimating, setIsAnimating] = useState(true);
	const currentIndexRef = useRef(0);
	const touchStartRef = useRef({ x: 0, y: 0 });
	const isHorizontalRef = useRef<boolean | null>(null);
	const didSwipeRef = useRef(false);
	const dragOffsetRef = useRef(0);

	// Build the image list.
	const images: GalleryImage[] = useMemo(() => {
		const matchingHero = (heroImages || []).find((image) => image.url === leadPhoto?.url);
		const sortedHeroes = (heroImages || [])
			.filter((image) => Boolean(image?.url) && image.url !== leadPhoto?.url)
			.sort((a, b) => IMAGES_ORDER.indexOf(a.kind ?? '') - IMAGES_ORDER.indexOf(b.kind ?? ''));
		return [
			...(leadPhoto?.url
				? [{ kind: matchingHero?.kind ?? 'exterior_front_driver', meta: leadPhoto.meta, url: leadPhoto.url }]
				: []),
			...sortedHeroes,
		];
	}, [heroImages, leadPhoto]);

	const canLoop = images.length >= 2;

	const navigateRef = useRef<(direction: Direction, source: 'button' | 'swipe') => void>(() => undefined);
	const goToRef = useRef<(nextIdx: number, direction: Direction, source: 'button' | 'swipe') => void>(() => undefined);

	const goTo = useCallback(
		(nextIdx: number, direction: Direction, source: 'button' | 'swipe') => {
			const currentKind = images[currentIndexRef.current]?.kind ?? '';
			const targetKind = images[nextIdx]?.kind ?? '';

			if (source === 'button') {
				if (direction === 1) {
					triggerVehicleCardNextImageClickedEvent({
						enquiryId,
						fromImage: currentKind,
						toImage: targetKind,
						ttlImages: images.length,
					});
				} else {
					triggerVehicleCardPrevImageClickedEvent({
						enquiryId,
						fromImage: currentKind,
						toImage: targetKind,
						ttlImages: images.length,
					});
				}
			}
			triggerVehicleCardImageLoadedEvent({
				enquiryId,
				fromImage: currentKind,
				toImage: targetKind,
				ttlImages: images.length,
			});

			currentIndexRef.current = nextIdx;
			setCurrentIndex(nextIdx);
		},
		[currentIndex, enquiryId, images],
	);

	const navigate = useCallback(
		(direction: Direction, source: 'button' | 'swipe') => {
			const len = images.length;
			const nextIdx = (currentIndex + direction + len) % len;
			const isLoop = (currentIndex === len - 1 && direction === 1) || (currentIndex === 0 && direction === -1);
			if (isLoop) setIsLooping(true);
			goTo(nextIdx, direction, source);
		},
		[currentIndex, goTo, images.length],
	);

	// Keep stable refs for use in touch effect (avoids re-attaching listeners on every slide).
	navigateRef.current = navigate;
	goToRef.current = goTo;

	const handleTraverse = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			event.preventDefault();
			event.stopPropagation();
			const direction: Direction = event.currentTarget.dataset.direction === 'right' ? 1 : -1;
			navigate(direction, 'button');
		},
		[navigate],
	);

	const onKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === 'ArrowLeft') {
				e.preventDefault();
				navigate(-1, 'button');
			}
			if (e.key === 'ArrowRight') {
				e.preventDefault();
				navigate(1, 'button');
			}
		},
		[navigate],
	);

	// Non-passive touch listeners for reliable preventDefault on iOS.
	useEffect(() => {
		const track = trackRef.current;
		if (!track) return undefined;

		const onTouchStart = (e: TouchEvent) => {
			const touch = e.touches[0];
			touchStartRef.current = { x: touch.clientX, y: touch.clientY };
			isHorizontalRef.current = null;
			didSwipeRef.current = false;
			dragOffsetRef.current = 0;
			setIsAnimating(false);
		};

		const onTouchMove = (e: TouchEvent) => {
			const touch = e.touches[0];
			const deltaX = touch.clientX - touchStartRef.current.x;
			const deltaY = touch.clientY - touchStartRef.current.y;

			// Determine direction on first significant move.
			if (
				isHorizontalRef.current === null &&
				(Math.abs(deltaX) > DIRECTION_LOCK_THRESHOLD || Math.abs(deltaY) > DIRECTION_LOCK_THRESHOLD)
			) {
				isHorizontalRef.current = Math.abs(deltaX) > Math.abs(deltaY);
			}

			if (!isHorizontalRef.current) return;

			// Block vertical scroll during horizontal swipe.
			// Guard with e.cancelable — the browser sets this to false when a scroll
			// is already in progress. Calling preventDefault() on a non-cancelable
			// event has no effect but triggers a console intervention warning.
			if (e.cancelable) {
				e.preventDefault();
			}
			didSwipeRef.current = true;
			dragOffsetRef.current = deltaX;

			// Update transform directly via DOM — no React re-render.
			const width = track.clientWidth || 1;
			const offsetPercent = (deltaX / width) * 100;
			const translateX = -(currentIndexRef.current * 100) + offsetPercent;
			track.style.transform = `translate3d(${translateX}%, 0, 0)`;
			track.style.transition = 'none';
		};

		const onTouchEnd = (e: TouchEvent) => {
			setIsAnimating(true);
			const deltaX = (e.changedTouches[0]?.clientX ?? 0) - touchStartRef.current.x;
			let didLoop = false;

			if (isHorizontalRef.current && Math.abs(deltaX) > SWIPE_THRESHOLD) {
				const direction: Direction = deltaX < 0 ? 1 : -1;
				const len = images.length;
				const isLoop =
					(currentIndexRef.current === len - 1 && direction === 1) ||
					(currentIndexRef.current === 0 && direction === -1);
				didLoop = isLoop;

				if (canLoop) {
					navigateRef.current(direction, 'swipe');
				} else {
					const nextIdx = currentIndexRef.current + direction;
					if (nextIdx >= 0 && nextIdx < len) {
						goToRef.current(nextIdx, direction, 'swipe');
					}
				}
			}

			// Animate to target position (or jump instantly for loops).
			const targetTranslate = -(currentIndexRef.current * 100);
			track.style.transition = didLoop ? 'none' : 'transform 0.3s ease-out';
			track.style.transform = `translate3d(${targetTranslate}%, 0, 0)`;
			dragOffsetRef.current = 0;
		};

		track.addEventListener('touchstart', onTouchStart, { passive: true });
		track.addEventListener('touchmove', onTouchMove, { passive: false });
		track.addEventListener('touchend', onTouchEnd, { passive: true });

		return () => {
			track.removeEventListener('touchstart', onTouchStart);
			track.removeEventListener('touchmove', onTouchMove);
			track.removeEventListener('touchend', onTouchEnd);
		};
	}, [canLoop, images.length]);

	// Reset isLooping after paint so subsequent navigations animate.
	useEffect(() => {
		if (isLooping) {
			const id = requestAnimationFrame(() => setIsLooping(false));
			return () => cancelAnimationFrame(id);
		}
		return undefined;
	}, [isLooping]);

	const translateX = -(currentIndex * 100);
	const transition = !isAnimating || isLooping ? 'none' : 'transform 0.3s ease-out';

	const galleryAriaLabel = vehiclePhotoAlt ? `${vehiclePhotoAlt} image gallery` : 'Vehicle image gallery';

	return (
		<section
			aria-label={galleryAriaLabel}
			className={cx(styles.trackWrapper, { [styles.fadeOut]: shouldApplyFadeOutStyle })}
			onKeyDown={onKeyDown}
		>
			<button
				aria-label="Previous image"
				className={styles.traverse}
				data-direction="left"
				onClick={handleTraverse}
				type="button"
			>
				<ArrowLeftIcon />
			</button>
			<button
				aria-label="Next image"
				className={styles.traverse}
				data-direction="right"
				onClick={handleTraverse}
				type="button"
			>
				<ArrowRightIcon />
			</button>
			<div
				ref={trackRef}
				className={styles.track}
				onClickCapture={(e) => {
					if (didSwipeRef.current) {
						e.preventDefault();
						e.stopPropagation();
					}
				}}
				style={{
					transform: `translate3d(${translateX}%, 0, 0)`,
					transition,
				}}
			>
				{images.map((image, index) => (
					<div key={`${image.url}-${index}`} className={styles.wrapper}>
						<LazyLoadImage
							alt={`${vehiclePhotoAlt ?? 'Vehicle image'} - ${index + 1}`}
							classNames={{ image: styles.image }}
							height={imageWidth * 0.75}
							photo={image}
							rootComponent="VehicleCardGallery"
							width={imageWidth}
						/>
					</div>
				))}
			</div>
		</section>
	);
};

export default VehicleCardGallery;
